import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Main as Container } from 'Components/Main';
import { PageLoaderList } from 'Components/PageLoader/List';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { Dialog, PageHeader, Select, TextField } from 'Components/ui';
import { NoData } from 'Components/NoData';
import { Controller, useForm } from 'react-hook-form';
import { getUsersName } from 'services/users-service';
import { InputLoader } from 'Components/PageLoader/Input';
import { callDeleteAction, callArchiveAction, callUnarchiveAction } from './actions';
import { PageHelper } from '../../Components/PageHelper';
import { getAreasShortPaged } from 'services/areas-service';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const BORDER = 'var(--vo-line)';
const BG = 'var(--vo-mist)';

/* ── Page header ── */
const AddBtn = styled.button`
  height: 44px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  flex-shrink: 0;
  &:hover { background: ${BLUE_DEEP}; }
`;

/* ── Info banner ── */
const InfoBanner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid ${BLUE};
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;
const InfoText = styled.div``;
const InfoTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;
const InfoDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0;
  line-height: 1.55;
`;

/* ── Filter card ── */
const FilterCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const FilterSectionTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  margin: 0 0 16px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;
const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
`;
const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
const FilterLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
`;
const FilterActions = styled.div`
  display: flex;
  gap: 10px;
`;
const ClearBtn = styled.button`
  height: 40px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const FilterBtn = styled.button`
  height: 40px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  &:hover { background: ${BLUE_DEEP}; }
`;

/* ── Table card ── */
const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;
const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const StyledThead = styled.thead``;
const TheadRow = styled.tr`
  background: ${BG};
`;
const Th = styled.th`
  padding: 12px 16px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-align: left;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;
const ThCenter = styled(Th)`
  text-align: center;
`;
const StyledTr = styled.tr`
  border-bottom: 1px solid ${BG};
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;
const Td = styled.td`
  padding: 12px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
`;
const TdBold = styled(Td)`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
`;
const TdSmall = styled(Td)`
  color: ${MUTED};
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const TdCenter = styled(Td)`
  text-align: center;
`;
const Badge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
`;
const ActionBtns = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;
const ActionBtn = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: ${MUTED};
  &:hover { background: ${BG}; color: ${INK}; }
`;
const ActionBtnDel = styled(ActionBtn)`
  &:hover { background: var(--vo-danger-10); color: var(--vo-danger); }
`;

/* ── Pagination ── */
const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-top: 1px solid ${BG};
`;
const PaginationCount = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;
const PageBtns = styled.div`
  display: flex;
  gap: 4px;
`;
const PageBtn = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1.5px solid ${({ $active }) => $active ? BLUE : BORDER};
  background: ${({ $active }) => $active ? BLUE : 'white'};
  color: ${({ $active }) => $active ? 'white' : MUTED};
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { border-color: ${BLUE}; color: ${({ $active }) => $active ? 'white' : BLUE}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const ArchiveSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
  </svg>
);

const UnarchiveSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/>
    <polyline points="10 12 12 10 14 12"/><line x1="12" y1="10" x2="12" y2="16"/>
  </svg>
);

const getCriticityStyle = (level) => {
  const map = {
    '1': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
    '2': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
    '3': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
    '4': { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
    '5': { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
  };
  return map[String(level)] || { color: MUTED, bg: BG };
};

const PAGE_SIZE = 15;
const DEFAULT_FORM = { name: '', criticity: '', user: '', status: 'active' };

export const Areas = () => {
  const { t } = useTranslation(['asm']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openUnarchiveModal, setOpenUnarchiveModal] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [itemToUnarchive, setItemToUnarchive] = useState(null);
  const [page, setPage] = useState(1);

  const { register, control, reset, getValues } = useForm({ defaultValues: DEFAULT_FORM });

  const getContentSearch = () => {
    const dados = getValues();
    let qs = '';
    if (dados.status === 'active') qs += '&archived=false';
    else if (dados.status === 'archived') qs += '&archived=true';
    if (dados.name) qs += `&name=${dados.name}`;
    if (dados.user) qs += `&user=${dados.user}`;
    if (dados.criticity) qs += `&criticity=${dados.criticity}`;
    return qs;
  };

  const { isLoading: userLoading, data: users } = useQuery(['getUsers'], async () => {
    if (!selectedTenant.slug) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { isLoading, error, data: pagedAreas } = useQuery(['getAreas', refetch, page], async () => {
    if (!selectedTenant.slug) return [];
    const result = await getAreasShortPaged(selectedTenant.slug, page, PAGE_SIZE, getContentSearch());
    if (result?.data?.data.length > 0) return result?.data;
    return { data: [], page, totalPages: 0 };
  });

  const onDelete = item => { setItemToDelete(item); setOpenDeleteModal(true); };
  const onCloseDeleteModal = () => { setItemToDelete(null); setOpenDeleteModal(false); };

  const onArchive = item => { setItemToArchive(item); setOpenArchiveModal(true); };
  const onCloseArchiveModal = () => { setItemToArchive(null); setOpenArchiveModal(false); };

  const onUnarchive = item => { setItemToUnarchive(item); setOpenUnarchiveModal(true); };
  const onCloseUnarchiveModal = () => { setItemToUnarchive(null); setOpenUnarchiveModal(false); };

  const onConfirmDeleteModal = async () => {
    try {
      await callDeleteAction(itemToDelete, onCloseDeleteModal, refetch, setRefetch);
    } catch (err) {
      onCloseDeleteModal();
      Dialog.alert({ title: t('asm:areas.delete.errorTitle'), message: err.response?.data?.message ?? err.message, icon: 'error' });
    }
  };

  const onConfirmArchiveModal = async () => {
    try {
      await callArchiveAction(itemToArchive, onCloseArchiveModal, refetch, setRefetch);
    } catch (err) {
      onCloseArchiveModal();
      Dialog.alert({ title: t('asm:areas.delete.errorTitle'), message: err.response?.data?.message ?? err.message, icon: 'error' });
    }
  };

  const onConfirmUnarchiveModal = async () => {
    try {
      await callUnarchiveAction(itemToUnarchive, onCloseUnarchiveModal, refetch, setRefetch);
    } catch (err) {
      onCloseUnarchiveModal();
      Dialog.alert({ title: t('asm:areas.delete.errorTitle'), message: err.response?.data?.message ?? err.message, icon: 'error' });
    }
  };

  const totalPages = pagedAreas?.totalPages || 0;
  const currentCount = pagedAreas?.data?.length || 0;
  const totalItems = pagedAreas?.total ?? (totalPages <= 1 ? currentCount : totalPages * PAGE_SIZE);

  const getPageButtons = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
    if (page >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', page - 1, page, page + 1, '...', totalPages];
  };

  return (
    <>
      <PageHelper steps={tourSteps} tourKey="areas-tour" autoStart={true} showButton={true} />
      <Container>
        <PageHeader
          data-tour="page-title"
          title={t('asm:areas.title')}
          segments={[
            { label: t('common:breadcrumbs.mapping') },
            { label: t('asm:areas.title') },
          ]}
          actions={
            <AddBtn data-tour="add-button" onClick={() => navigate('/areas/add')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              {t('asm:areas.addButton')}
            </AddBtn>
          }
        />

        <div data-tour="info-section">
          <InfoBanner>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <polygon points="12 2 2 7 12 12 22 7 12 2"/>
              <polyline points="2 17 12 22 22 17"/>
              <polyline points="2 12 12 17 22 12"/>
            </svg>
            <InfoText>
              <InfoTitle>{t('asm:areas.infoTitle')}</InfoTitle>
              <InfoDesc>{t('asm:areas.infoDescription')}</InfoDesc>
            </InfoText>
          </InfoBanner>
        </div>

        {!error && (
          <div data-tour="filters-card">
            <FilterCard>
              <FilterSectionTitle>{t('asm:areas.filter.sectionTitle')}</FilterSectionTitle>
              <FilterGrid>
                <FilterField>
                  <FilterLabel>{t('asm:areas.filter.name')}</FilterLabel>
                  <TextField fullWidth {...register('name')} placeholder={t('asm:areas.filter.namePlaceholder')} />
                </FilterField>
                <FilterField>
                  <FilterLabel>{t('asm:areas.filter.criticality')}</FilterLabel>
                  <Controller
                    control={control}
                    name="criticity"
                    render={({ field: { onChange, value } }) => (
                      <Select
                        name="criticity"
                        value={value}
                        onChange={onChange}
                        options={[
                          { value: '', label: t('asm:areas.filter.criticalityPlaceholder') },
                          ...['1', '2', '3', '4', '5'].map(item => ({ value: item, label: t('asm:criticality.' + item) })),
                        ]}
                      />
                    )}
                  />
                </FilterField>
                <FilterField>
                  <FilterLabel>{t('asm:areas.filter.responsible')}</FilterLabel>
                  {!userLoading ? (
                    <Controller
                      control={control}
                      name="user"
                      render={({ field: { onChange, value } }) => (
                        <Select
                          name="user"
                          value={value}
                          onChange={onChange}
                          options={[
                            { value: '', label: t('asm:areas.filter.responsiblePlaceholder') },
                            ...(users || []).map(u => ({ value: u.name, label: u.name })),
                          ]}
                        />
                      )}
                    />
                  ) : (
                    <InputLoader height="40px" />
                  )}
                </FilterField>
                <FilterField>
                  <FilterLabel>{t('asm:areas.filter.status')}</FilterLabel>
                  <Controller
                    control={control}
                    name="status"
                    render={({ field: { onChange, value } }) => (
                      <Select
                        name="status"
                        value={value}
                        onChange={onChange}
                        options={[
                          { value: 'all', label: t('asm:areas.filter.statusAll') },
                          { value: 'active', label: t('asm:areas.filter.statusActive') },
                          { value: 'archived', label: t('asm:areas.filter.statusArchived') },
                        ]}
                      />
                    )}
                  />
                </FilterField>
              </FilterGrid>
              <FilterActions data-tour="filter-buttons">
                <ClearBtn onClick={() => { reset(DEFAULT_FORM); setPage(1); setRefetch(r => !r); }}>
                  {t('asm:areas.filter.clear')}
                </ClearBtn>
                <FilterBtn onClick={() => { setPage(1); setRefetch(r => !r); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  {t('asm:areas.filter.filter')}
                </FilterBtn>
              </FilterActions>
            </FilterCard>
          </div>
        )}

        {isLoading && <PageLoaderList />}
        {error && <div>{t('asm:areas.error')}</div>}
        {!isLoading && !error && pagedAreas?.data?.length === 0 && <NoData description={t('asm:areas.noData')} />}

        {!isLoading && !error && pagedAreas?.data?.length > 0 && (
          <div data-tour="areas-table">
            <TableCard>
              <StyledTable>
                <StyledThead>
                  <TheadRow>
                    <Th>{t('asm:areas.table.name')}</Th>
                    <Th>{t('asm:areas.table.criticality')}</Th>
                    <Th>{t('asm:areas.table.description')}</Th>
                    <Th>{t('asm:areas.table.responsible')}</Th>
                    <ThCenter>{t('asm:areas.table.actions')}</ThCenter>
                  </TheadRow>
                </StyledThead>
                <tbody>
                  {pagedAreas.data.map((area) => {
                    const critStyle = getCriticityStyle(area.criticity);
                    const areaId = area._id || area.id;
                    return (
                      <StyledTr key={areaId}>
                        <TdBold>{area.name}</TdBold>
                        <Td>
                          {area.criticity ? (
                            <Badge $color={critStyle.color} $bg={critStyle.bg}>
                              {t('asm:criticality.' + area.criticity)}
                            </Badge>
                          ) : '—'}
                        </Td>
                        <TdSmall title={area.description}>{area.description || '—'}</TdSmall>
                        <Td>{area.user || '—'}</Td>
                        <TdCenter>
                          <ActionBtns>
                            <ActionBtn onClick={() => navigate(`/areas/show/${areaId}`)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                              </svg>
                            </ActionBtn>
                            <ActionBtn onClick={() => navigate(`/areas/edit/${areaId}`)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </ActionBtn>
                            {!area.archived ? (
                              <ActionBtn onClick={() => onArchive(area)}>
                                <ArchiveSvg />
                              </ActionBtn>
                            ) : (
                              <ActionBtn onClick={() => onUnarchive(area)}>
                                <UnarchiveSvg />
                              </ActionBtn>
                            )}
                            <ActionBtnDel onClick={() => onDelete(area)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            </ActionBtnDel>
                          </ActionBtns>
                        </TdCenter>
                      </StyledTr>
                    );
                  })}
                </tbody>
              </StyledTable>

              {totalPages > 0 && (
                <div data-tour="pagination">
                  <PaginationRow>
                    <PaginationCount>
                      {t('asm:areas.pagination.showing', { count: currentCount, total: totalItems })}
                    </PaginationCount>
                    <PageBtns>
                      <PageBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 18 9 12 15 6"/>
                        </svg>
                      </PageBtn>
                      {getPageButtons().map((btn, i) =>
                        btn === '...' ? (
                          <PageBtn key={`ell-${i}`} disabled style={{ border: 'none', background: 'none', color: MUTED }}>…</PageBtn>
                        ) : (
                          <PageBtn key={btn} $active={btn === page} onClick={() => setPage(btn)}>{btn}</PageBtn>
                        )
                      )}
                      <PageBtn onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </PageBtn>
                    </PageBtns>
                  </PaginationRow>
                </div>
              )}
            </TableCard>

            <Dialog
              open={openDeleteModal}
              onConfirm={onConfirmDeleteModal}
              onCancel={onCloseDeleteModal}
              message={`${t('asm:areas.delete.confirm')} ${itemToDelete?.name}?`}
            />
            <Dialog
              open={openArchiveModal}
              onConfirm={onConfirmArchiveModal}
              onCancel={onCloseArchiveModal}
              message={`${t('asm:areas.delete.archive')} ${itemToArchive?.name}?`}
            />
            <Dialog
              open={openUnarchiveModal}
              onConfirm={onConfirmUnarchiveModal}
              onCancel={onCloseUnarchiveModal}
              message={`${t('asm:areas.delete.unarchive')} ${itemToUnarchive?.name}?`}
            />
          </div>
        )}
      </Container>
    </>
  );
};

const tourSteps = [
  { target: 'body', content: 'Bem-vindo à página de Áreas de Negócio! Aqui você pode gerenciar as diferentes áreas, setores ou squads da sua organização.', placement: 'center', disableBeacon: true },
  { target: '[data-tour="page-title"]', content: 'Este é o título da página.', disableScrolling: true },
  { target: '[data-tour="add-button"]', content: 'Clique neste botão para adicionar uma nova área de negócio.', disableScrolling: true },
  { target: '[data-tour="info-section"]', content: 'Esta seção explica o propósito das áreas de negócio.', disableScrolling: true },
  { target: '[data-tour="filters-card"]', content: 'Use estes filtros para buscar áreas específicas por nome, criticidade, responsável ou status.' },
  { target: '[data-tour="filter-buttons"]', content: 'Use "Limpar" para resetar os filtros ou "Filtrar" para aplicar a busca.' },
  { target: '[data-tour="areas-table"]', content: 'Esta tabela exibe todas as áreas de negócio cadastradas.' },
  { target: '[data-tour="pagination"]', content: 'Use a paginação para navegar entre as páginas.' },
  { target: 'body', content: 'Tour concluído!', placement: 'center' },
];
